/**
 * Utility for dynamically loading Google Fonts with caching and state management
 */

class FontLoader {
  constructor() {
    this.loadedFonts = new Set();
    this.loadingFonts = new Set();
    this.failedFonts = new Set();
    this.observers = new Map(); // font name -> Set of callbacks
  }

  /**
   * Load a Google Font dynamically
   * @param {string} fontName - Name of the Google Font to load
   * @returns {Promise<boolean>} - Resolves to true if loaded successfully, false otherwise
   */
  async loadFont(fontName) {
    // Skip if already loaded
    if (this.loadedFonts.has(fontName)) {
      return true;
    }

    // Skip if currently loading - return existing promise
    if (this.loadingFonts.has(fontName)) {
      return new Promise(resolve => {
        this.addObserver(fontName, resolve);
      });
    }

    // Skip if previously failed (could add retry logic here)
    if (this.failedFonts.has(fontName)) {
      return false;
    }

    this.loadingFonts.add(fontName);

    try {
      // Create link element for Google Fonts
      const link = document.createElement('link');
      link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName.replace(/ /g, '+'))}:wght@400&display=swap`;
      link.rel = 'stylesheet';
      
      // Promise to track loading
      const loadPromise = new Promise((resolve, reject) => {
        link.onload = () => resolve(true);
        link.onerror = () => reject(new Error(`Failed to load font: ${fontName}`));
        
        // Fallback timeout
        setTimeout(() => reject(new Error(`Font loading timeout: ${fontName}`)), 5000);
      });

      // Add to document head
      document.head.appendChild(link);

      // Wait for font to load
      await loadPromise;

      // Verify font is actually available using FontFace API if supported
      if ('fonts' in document) {
        await document.fonts.load(`400 16px "${fontName}"`);
      }

      this.loadedFonts.add(fontName);
      this.loadingFonts.delete(fontName);
      
      // Notify observers
      this.notifyObservers(fontName, true);
      
      return true;
    } catch (error) {
      console.warn(`Failed to load font ${fontName}:`, error);
      this.failedFonts.add(fontName);
      this.loadingFonts.delete(fontName);
      
      // Notify observers
      this.notifyObservers(fontName, false);
      
      return false;
    }
  }

  /**
   * Load multiple fonts in parallel
   * @param {string[]} fontNames - Array of font names to load
   * @returns {Promise<boolean[]>} - Array of results for each font
   */
  async loadFonts(fontNames) {
    return Promise.all(fontNames.map(font => this.loadFont(font)));
  }

  /**
   * Check if a font is loaded
   * @param {string} fontName - Name of the font to check
   * @returns {boolean}
   */
  isFontLoaded(fontName) {
    return this.loadedFonts.has(fontName);
  }

  /**
   * Check if a font is currently loading
   * @param {string} fontName - Name of the font to check
   * @returns {boolean}
   */
  isFontLoading(fontName) {
    return this.loadingFonts.has(fontName);
  }

  /**
   * Check if a font failed to load
   * @param {string} fontName - Name of the font to check
   * @returns {boolean}
   */
  isFontFailed(fontName) {
    return this.failedFonts.has(fontName);
  }

  /**
   * Get the loading state of a font
   * @param {string} fontName - Name of the font to check
   * @returns {'loaded'|'loading'|'failed'|'idle'}
   */
  getFontState(fontName) {
    if (this.loadedFonts.has(fontName)) return 'loaded';
    if (this.loadingFonts.has(fontName)) return 'loading';
    if (this.failedFonts.has(fontName)) return 'failed';
    return 'idle';
  }

  /**
   * Add observer for font loading state changes
   * @param {string} fontName - Font to observe
   * @param {function} callback - Callback function (receives boolean success)
   */
  addObserver(fontName, callback) {
    if (!this.observers.has(fontName)) {
      this.observers.set(fontName, new Set());
    }
    this.observers.get(fontName).add(callback);
  }

  /**
   * Remove observer for font loading state changes
   * @param {string} fontName - Font to stop observing
   * @param {function} callback - Callback function to remove
   */
  removeObserver(fontName, callback) {
    if (this.observers.has(fontName)) {
      this.observers.get(fontName).delete(callback);
    }
  }

  /**
   * Notify all observers of a font's loading completion
   * @private
   */
  notifyObservers(fontName, success) {
    if (this.observers.has(fontName)) {
      this.observers.get(fontName).forEach(callback => {
        try {
          callback(success);
        } catch (error) {
          console.error('Error in font loading observer:', error);
        }
      });
      // Clear observers after notification
      this.observers.delete(fontName);
    }
  }

  /**
   * Preload popular fonts or fonts likely to be used
   * @param {string[]} fontNames - Array of font names to preload
   */
  preloadFonts(fontNames) {
    // Load fonts in background without waiting
    fontNames.forEach(fontName => {
      this.loadFont(fontName).catch(() => {
        // Silently handle preload failures
      });
    });
  }

  /**
   * Clear all loaded fonts (useful for testing or memory management)
   */
  clearCache() {
    this.loadedFonts.clear();
    this.loadingFonts.clear();
    this.failedFonts.clear();
    this.observers.clear();
  }
}

// Create singleton instance
const fontLoader = new FontLoader();

export default fontLoader;